import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { loadMe } from './auth/session';
import { BrandTheme } from './components/brand-theme';
import { StaffIntlProvider } from './i18n/provider';
import { reportRenderError } from './lib/error-reporting';
import { AppShell } from './routes/app-shell';
import { LoginPage } from './routes/login';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: function Root() {
    return (
      <StaffIntlProvider>
        <BrandTheme />
        <Outlet />
      </StaffIntlProvider>
    );
  },
});

// Страницы с параметром пути — React.lazy внутри обёртки: она читает параметр из маршрута.
const PointDetailPage = lazy(() =>
  import('./routes/point-detail').then((module) => ({ default: module.PointDetailPage })),
);
const UserDetailPage = lazy(() =>
  import('./routes/user-detail').then((module) => ({ default: module.UserDetailPage })),
);
const CustomerDetailPage = lazy(() =>
  import('./routes/customer-detail').then((module) => ({ default: module.CustomerDetailPage })),
);
const OrderDetailPage = lazy(() =>
  import('./routes/order-detail').then((module) => ({ default: module.OrderDetailPage })),
);
const ScanOrderPage = lazy(() =>
  import('./routes/scan').then((module) => ({ default: module.ScanPage })),
);
const ImportDetailPage = lazy(() =>
  import('./routes/import-detail').then((module) => ({ default: module.ImportDetailPage })),
);
const ProductPage = lazy(() =>
  import('./routes/catalog-product').then((module) => ({ default: module.ProductPage })),
);
const RoleDetailPage = lazy(() =>
  import('./routes/role-detail').then((module) => ({ default: module.RoleDetailPage })),
);

/* ---------- вход ---------- */

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Без zod: роутер входит в стартовый бандл.
  // `sent` — код из письма уже отправлен этим входом (иначе экран предложит отправить).
  validateSearch: (
    search: Record<string, unknown>,
  ): { step?: 'password' | 'code'; sent?: true } => ({
    ...(search.step === 'password' || search.step === 'code' ? { step: search.step } : {}),
    ...(search.sent === true ? { sent: true as const } : {}),
  }),
  beforeLoad: async ({ context, search }) => {
    const me = await loadMe(context.queryClient);
    if (me?.session.state === 'active') throw redirect({ to: '/' });
    if (me?.session.state === 'mfa_enrollment') throw redirect({ to: '/enroll' });
    // Сессия до второго фактора — сразу экран кода; без сессии шаг кода бессмыслен.
    const step = me?.session.state === 'mfa_pending' ? 'code' : 'password';
    if (search.step !== step) throw redirect({ to: '/login', search: { step } });
  },
  component: function Login() {
    const { step, sent } = loginRoute.useSearch();
    return <LoginPage step={step ?? 'password'} sent={sent ?? false} />;
  },
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: lazyRouteComponent(() => import('./routes/forgot-password'), 'ForgotPasswordPage'),
});

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  component: lazyRouteComponent(() => import('./routes/setup'), 'SetupPage'),
});

const enrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/enroll',
  beforeLoad: async ({ context }) => {
    const me = await loadMe(context.queryClient);
    if (!me) throw redirect({ to: '/login' });
    if (me.session.state !== 'mfa_enrollment') throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('./routes/enroll'), 'EnrollPage'),
});

/* ---------- приложение (вход завершён) ---------- */

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context }) => {
    const me = await loadMe(context.queryClient);
    if (!me || me.session.state === 'mfa_pending') throw redirect({ to: '/login' });
    if (me.session.state === 'mfa_enrollment') throw redirect({ to: '/enroll' });
  },
  component: AppShell,
});

const statusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./routes/status-page'), 'StatusPage'),
});
const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/users',
  component: lazyRouteComponent(() => import('./routes/users'), 'UsersPage'),
});
const userRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/users/$userId',
  component: function User() {
    const { userId } = userRoute.useParams();
    return (
      <Suspense fallback={null}>
        <UserDetailPage key={userId} userId={userId} />
      </Suspense>
    );
  },
});
const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers',
  component: lazyRouteComponent(() => import('./routes/customers'), 'CustomersPage'),
});
const customerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers/$customerId',
  component: function CustomerDetail() {
    const { customerId } = customerRoute.useParams();
    return (
      <Suspense fallback={null}>
        <CustomerDetailPage key={customerId} customerId={customerId} />
      </Suspense>
    );
  },
});
const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orders',
  component: lazyRouteComponent(() => import('./routes/orders'), 'OrdersPage'),
});
const orderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orders/$orderId',
  component: function OrderDetail() {
    const { orderId } = orderRoute.useParams();
    return (
      <Suspense fallback={null}>
        <OrderDetailPage key={orderId} orderId={orderId} />
      </Suspense>
    );
  },
});
const cashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cash',
  component: lazyRouteComponent(() => import('./routes/cash'), 'CashPage'),
});
const productionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/production',
  component: lazyRouteComponent(() => import('./routes/production'), 'ProductionPage'),
});
const scanRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/scan',
  component: lazyRouteComponent(() => import('./routes/scan'), 'ScanPage'),
});
// Адрес QR на чеке и этикетке (ADR-053).
const scanOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/scan/$orderId',
  component: function ScanOrder() {
    const { orderId } = scanOrderRoute.useParams();
    return (
      <Suspense fallback={null}>
        <ScanOrderPage key={orderId} initialCode={orderId} />
      </Suspense>
    );
  },
});
const catalogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/catalog',
  component: lazyRouteComponent(() => import('./routes/catalog'), 'CatalogPage'),
});
const productRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/catalog/products/$productId',
  component: function ProductDetail() {
    const { productId } = productRoute.useParams();
    return (
      <Suspense fallback={null}>
        <ProductPage key={productId} productId={productId} />
      </Suspense>
    );
  },
});
const pricingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pricing',
  component: lazyRouteComponent(() => import('./routes/pricing'), 'PricingPage'),
});
const schedulingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/scheduling',
  component: lazyRouteComponent(() => import('./routes/scheduling'), 'SchedulingPage'),
});
const importsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/imports',
  component: lazyRouteComponent(() => import('./routes/imports'), 'ImportsPage'),
});
const importRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/imports/$jobId',
  component: function ImportDetail() {
    const { jobId } = importRoute.useParams();
    return (
      <Suspense fallback={null}>
        <ImportDetailPage key={jobId} jobId={jobId} />
      </Suspense>
    );
  },
});
const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/roles',
  component: lazyRouteComponent(() => import('./routes/roles'), 'RolesPage'),
});
const roleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/roles/$roleId',
  component: function RoleDetail() {
    const { roleId } = roleRoute.useParams();
    return (
      <Suspense fallback={null}>
        <RoleDetailPage key={roleId} roleId={roleId} />
      </Suspense>
    );
  },
});
const pointsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/points',
  component: lazyRouteComponent(() => import('./routes/points'), 'PointsPage'),
});
const pointRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/points/$pointId',
  component: function PointDetail() {
    const { pointId } = pointRoute.useParams();
    return (
      <Suspense fallback={null}>
        <PointDetailPage key={pointId} pointId={pointId} />
      </Suspense>
    );
  },
});
const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  component: lazyRouteComponent(() => import('./routes/audit'), 'AuditPage'),
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('./routes/settings'), 'SettingsPage'),
});
const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/profile',
  component: lazyRouteComponent(() => import('./routes/profile'), 'ProfilePage'),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  setupRoute,
  enrollRoute,
  appRoute.addChildren([
    statusRoute,
    ordersRoute,
    orderRoute,
    cashRoute,
    productionRoute,
    scanRoute,
    scanOrderRoute,
    usersRoute,
    userRoute,
    customersRoute,
    customerRoute,
    catalogRoute,
    productRoute,
    pricingRoute,
    schedulingRoute,
    importsRoute,
    importRoute,
    rolesRoute,
    roleRoute,
    pointsRoute,
    pointRoute,
    auditRoute,
    settingsRoute,
    profileRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: false,
    defaultOnCatch: reportRenderError,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
